import { z } from "zod";

const address = z.string().regex(/^0x[0-9a-f]{40}$/);
const hash = z.string().regex(/^0x[0-9a-f]{64}$/);
const raw = z
  .string()
  .max(78)
  .regex(/^(0|[1-9][0-9]*)$/)
  .refine(
    (value) => /^(0|[1-9][0-9]*)$/.test(value) && BigInt(value) < 2n ** 256n,
  );
const positiveRaw = raw.refine(
  (value) => /^[1-9][0-9]*$/.test(value) && BigInt(value) > 0n,
);
const chain = z.string().regex(/^eip155:[1-9][0-9]*$/);
const asset = z.string().regex(/^eip155:[1-9][0-9]*\/erc20:0x[0-9a-f]{40}$/);
const account = z.string().regex(/^eip155:[1-9][0-9]*:0x[0-9a-f]{40}$/);

/** Immutable execution commitment resolved from an existing position instrument. */
export const VaultBindingSchema = z
  .object({
    adapter: z.literal("erc4626-v1"),
    /** Omitted for conventional ERC-4626 max-function capacity checks. */
    capacityPolicy: z.literal("morpho-v2").optional(),
    instrumentId: z.string().min(1),
    registryId: z.string().min(1),
    venueId: z.string().min(1),
    chainRef: chain,
    vaultAddress: address,
    underlyingAssetId: asset,
    receiptAssetId: asset,
    underlyingDecimals: z.number().int().min(0).max(255),
    receiptDecimals: z.number().int().min(0).max(255),
    ownerAccountRef: account,
    settlementAccountRef: account,
    executorAddress: address,
    observation: z
      .object({
        blockNumber: raw,
        blockHash: hash,
        vaultCodeHash: hash,
        executorCodeHash: hash,
        adapterCodeHash: hash,
        observedAt: z.string().datetime(),
        validUntil: z.string().datetime(),
      })
      .strict(),
  })
  .strict()
  .superRefine((binding, ctx) => {
    if (
      [binding.underlyingAssetId, binding.receiptAssetId].some(
        (id) => !id.startsWith(`${binding.chainRef}/`),
      ) ||
      [binding.ownerAccountRef, binding.settlementAccountRef].some(
        (id) => !id.startsWith(`${binding.chainRef}:`),
      )
    ) {
      ctx.addIssue({
        code: "custom",
        message: "vault binding identities must share the execution chain",
      });
    }
    if (
      binding.receiptAssetId !==
        `${binding.chainRef}/erc20:${binding.vaultAddress}` ||
      binding.underlyingAssetId === binding.receiptAssetId
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "vault receipt must identify the ERC-4626 vault and differ from its underlying",
      });
    }
    if (
      binding.ownerAccountRef === binding.settlementAccountRef ||
      Date.parse(binding.observation.validUntil) <=
        Date.parse(binding.observation.observedAt)
    ) {
      ctx.addIssue({
        code: "custom",
        message:
          "vault binding requires separate settlement custody and an observation expiry",
      });
    }
  });

/** Deposit input is underlying; redeem input is shares. Receiver is always owner. */
export const VaultOperationSchema = z
  .object({
    kind: z.enum(["deposit", "redeem"]),
    binding: VaultBindingSchema,
    amountRaw: positiveRaw,
    minOutputRaw: positiveRaw,
    /** Exact-amount executable preview, distinct from whole-position valuation. */
    previewOutputRaw: positiveRaw,
  })
  .strict()
  .superRefine((operation, ctx) => {
    if (
      /^[0-9]+$/.test(operation.minOutputRaw) &&
      /^[0-9]+$/.test(operation.previewOutputRaw) &&
      BigInt(operation.minOutputRaw) > BigInt(operation.previewOutputRaw)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "vault output floor exceeds its executable preview",
      });
    }
  });

export type VaultBinding = z.infer<typeof VaultBindingSchema>;
export type VaultOperation = z.infer<typeof VaultOperationSchema>;
